import type { StyleValue } from 'vue'

import { computed } from 'vue'

/** The two page widths. See PjContainer for what each is for. */
const PJ_CONTAINER_WIDTH = {
  NARROW: 'narrow',
  WIDE: 'wide'
} as const

type PjContainerWidth = (typeof PJ_CONTAINER_WIDTH)[keyof typeof PJ_CONTAINER_WIDTH]

/**
 * Binds an element's page width, for anything that has to line up with the
 * container but cannot be inside it.
 *
 * That is every piece of chrome across the top of a screen: the tab bar, the
 * venue bar, the category strip. Each is a full-width band — a border that spans
 * the window, a background that survives a scroll — so its contents are centred
 * to the page width instead, and if that width disagrees with the page's the
 * result is a wordmark or a tab sitting off-column from what it labels.
 *
 * Set as an inline custom property rather than left to `.pj-container`'s own
 * fallback, so the value is stated where it is used. A silently-resolving var
 * chain is a thing that fails invisibly and looks like a layout bug.
 *
 * The name matters. This was `--pj-container` for one commit, which is also the
 * design system's tonal-green surface token — so setting it inline on a page
 * container inherited a *length* into every descendant asking for that *colour*,
 * and every tonal button, active tab pill and table chip inside a page silently
 * lost its background. Anything added here belongs in the `--pj-page-*` space,
 * which is ours; the unprefixed names are the designer's.
 */
export const useContainerWidth = (width: () => PjContainerWidth | undefined) =>
  computed<StyleValue>(() => ({ '--pj-page-max': `var(--pj-page-${width() ?? 'wide'})` }))

export { PJ_CONTAINER_WIDTH }
export type { PjContainerWidth }
